import Button from "@builder/components/general/Button";
import Icon from "@builder/components/general/Icon";
import OliverText from "@builder/components/general/OliverText";
import Toggle from "@builder/components/general/Toggle";
import { useOutsideClick } from "@builder/hooks/useOutsideClick";
import { getFrameSize } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { setBuildSize, setFrameSize } from "@builder/store/slices/BuilderSlice/builderSlice";
import { setDeviceSkin } from "@builder/store/slices/frameTransformSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { buildSizes } from "@common/build/constants";
import { BuildSizes, GlobalBlockTypes } from "@common/build/types";
import { ReactNode, useEffect, useRef, useState } from "react";
import OliverImage from "../../OliverImage";
import "./style.scss";
import { useTranslation } from "react-i18next";
import TooltipElement from "@builder/components/general/Tooltip";

const isSameSize = (
    buildSize: { key: BuildSizes; frameSize?: { x: number; y: number } },
    xInputValue: number,
    yInputValue: number | "fit-content",
    isRotatable: boolean
) => {
    if (isRotatable) {
        return [buildSizes[buildSize.key]?.value.x, buildSizes[buildSize.key]?.value.y].every(val => [yInputValue, xInputValue].includes(val));
    } else {
        return buildSizes[buildSize.key]?.value.x === xInputValue && buildSizes[buildSize.key]?.value.y === yInputValue;
    }
};

const commonSizes = Object.fromEntries(Object.entries(buildSizes).filter(([_, value]) => value.type !== GlobalBlockTypes.GB_PRINT));

const groupedSizes = Object.entries(commonSizes).reduce((acc, [key, size]) => {
    if (!size.type) return acc;
    if (!acc[size.type]) {
        acc[size.type] = [];
    }
    acc[size.type].push({ ...size, originalKey: key });
    return acc;
    // eslint-disable-next-line
}, {} as Record<string, any[]>);

function SizeCollapse({ title, children }: { title: string; children: ReactNode }) {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const handleToggle = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <div className="size-collapse">
            <div className="title" onClick={handleToggle}>
                <div className="title-content">
                    <OliverImage src="/angled-arrow-down.svg" className={`collapse-arrow ${isCollapsed ? "collapsed" : ""}`} />
                    <OliverText text={title} size="xxs" color="base-grey" translatePrefix="frameSize." />
                </div>
            </div>
            {!isCollapsed && <div className="body">{children}</div>}
        </div>
    );
}

function FrameSizeIcon({ buildSizeKey, xInput, yInput }: { buildSizeKey: BuildSizes; xInput?: number; yInput?: number }) {
    if (buildSizeKey && buildSizes[buildSizeKey]?.type === "Desktop") {
        return <Icon src="./desktop-icon.svg" />;
    }
    const rotation = xInput && yInput && xInput > yInput ? 90 : 0;
    return <Icon src="./tablet-portrait-icon.svg" iconStyle={{ transform: `rotate(${rotation}deg)` }} />;
}

const FrameSize: React.FC = () => {
    const dispatch = useAppDispatch();
    const buildSize = useAppSelector(state => state.builder.build.size);
    const frameSize = useAppSelector(getFrameSize);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [xInput, setXInput] = useState<{ value: number; error: string }>({ value: 0, error: "" });
    const [yInput, setYInput] = useState<{ value: number | "fit-content"; error: string }>({ value: 0, error: "" });
    const menuRef = useRef<HTMLDivElement>(null);
    const xErrorRef = useRef<HTMLDivElement>(null);
    const yErrorRef = useRef<HTMLDivElement>(null);
    const deviceSkin = useAppSelector(state => state.frameTransform.deviceSkin);
    const { t } = useTranslation();

    const rotatable = buildSize.key !== "custom" && buildSizes[buildSize.key]?.type !== "Desktop" && !buildSizes[buildSize.key]?.noRotate;

    useEffect(() => {
        if (buildSize.key === "custom") {
            dispatch(setDeviceSkin(false));
        }
    }, [buildSize.key]);

    useOutsideClick(menuRef, () => {
        setMenuOpen(false);
    });

    useOutsideClick(xErrorRef, () => {
        setXInput({ value: xInput.value, error: "" });
    });

    useOutsideClick(yErrorRef, () => {
        setYInput({ value: yInput.value, error: "" });
    });

    useEffect(() => {
        setXInput({ value: frameSize.x, error: "" });
        setYInput({ value: frameSize.y, error: "" });
    }, [frameSize]);

    const handleFrameSizeChange = (e: React.MouseEvent<HTMLButtonElement>) => {
        const dataKey = e.currentTarget.getAttribute("data-key") as BuildSizes;
        if (dataKey) {
            const newFrameSize = buildSizes[dataKey];
            if (newFrameSize) {
                if (dataKey !== buildSize.key) {
                    dispatch(setFrameSize(newFrameSize.value));
                    if (typeof newFrameSize.value.y === "number") {
                        dispatch(setBuildSize({ key: dataKey, frameSize: newFrameSize.value as { x: number; y: number } }));
                    } else {
                        dispatch(setBuildSize({ key: dataKey }));
                    }
                }
            }
        }
    };

    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        const newValue = parseInt(e.currentTarget.value);
        if (typeof newValue === "number" && !isNaN(newValue)) {
            if (e.currentTarget.id === "xFrameSizeInput") {
                setXInput({ value: newValue, error: "" });
            } else if (e.currentTarget.id === "yFrameSizeInput") {
                setYInput({ value: newValue, error: "" });
            }
        }
    };

    const setCustomFrameSize = () => {
        let inputCheck = true;
        const maxXY = 5120;
        const minXY = 300;
        if (xInput.value > maxXY) {
            inputCheck = false;
            setXInput({ value: xInput.value, error: t("generalComponents.input.Input value is too high, keep it under") + " " + maxXY });
        } else if (xInput.value < minXY) {
            inputCheck = false;
            setXInput({ value: xInput.value, error: t("generalComponents.input.Input value is too low, keep it above") + " " + minXY });
        }
        if (yInput.value !== "fit-content" && yInput.value > maxXY) {
            inputCheck = false;
            setYInput({ value: yInput.value, error: t("generalComponents.input.Input value is too high, keep it under") + " " + maxXY });
        } else if (yInput.value !== "fit-content" && yInput.value < minXY) {
            inputCheck = false;
            setYInput({ value: yInput.value, error: t("generalComponents.input.Input value is too low, keep it above") + " " + minXY });
        }

        if (inputCheck) {
            dispatch(setFrameSize({ x: xInput.value, y: yInput.value }));
            dispatch(setBuildSize({ key: "custom", frameSize: { x: xInput.value as number, y: yInput.value as number } }));
        }
    };

    Toggle;
    const toggleCallback = (checked: boolean) => {
        dispatch(setDeviceSkin(checked));
    };

    const handleRotate = () => {
        if (frameSize.y === "fit-content") return;
        const newFrameSize = { x: frameSize.y, y: frameSize.x };
        dispatch(setFrameSize(newFrameSize));
        if (yInput.value !== "fit-content") {
            dispatch(setBuildSize({ key: buildSize.key, frameSize: { x: yInput.value as number, y: xInput.value as number } }));
        } else {
            dispatch(setBuildSize({ key: buildSize.key }));
        }
    };

    return (
        <div className="frame-size-wrapper" ref={menuRef}>
            <TooltipElement text="Canvas size" translationPrefix="generalComponents.head.">
            <button className={menuOpen ? "menu-open" : ""} onClick={() => setMenuOpen(!menuOpen)}>
                <FrameSizeIcon buildSizeKey={buildSize.key} xInput={xInput.value} yInput={yInput.value === "fit-content" ? 0 : yInput.value} />
                <OliverText text={`${frameSize.x}x${frameSize.y}`} color="base-grey" size="s" weight="medium" />
            </button>
            </TooltipElement>
            {menuOpen && (
                <div className="menu-wrapper">
                    <div className="frame-size-menu">
                        <div className="left-col">
                            <div className="body">
                                <div className="common-s">
                                    <OliverText text="Sizes" size="xs" color="base-blue" translatePrefix="frameSize." className="main-t"></OliverText>
                                    <div className="sizes">
                                        {Object.entries(groupedSizes).map(([type, sizes]) => (
                                            <SizeCollapse key={type} title={type}>
                                                {sizes.map(size => (
                                                    <button key={size.originalKey} data-key={size.originalKey} onClick={handleFrameSizeChange}>
                                                        <OliverText
                                                            text={size.label}
                                                            size="s"
                                                            color={size.originalKey === buildSize.key ? "blue" : "base-blue"}
                                                        />
                                                        <OliverText text={`${size.value.x}px by ${size.value.y}px`} size="xs" color="base-grey" />
                                                    </button>
                                                ))}
                                            </SizeCollapse>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="right-col">
                            <OliverText text="Custom Size" size="xs" color="base-blue" translatePrefix="frameSize." className="main-t"></OliverText>
                            <label>
                                <OliverText text="Width" size="xxs" weight="semi-bold" color="base-blue" translatePrefix="frameSize." />
                                <input
                                    id="xFrameSizeInput"
                                    type="text"
                                    value={xInput.value}
                                    onInput={handleInput}
                                    className={xInput.error ? "input-error" : ""}
                                />
                                <OliverText text="px" size="xxs" weight="semi-bold" color="base-blue" className="static-text" />
                            </label>
                            <label>
                                <OliverText text="Height" size="xxs" weight="semi-bold" color="base-blue" translatePrefix="frameSize." />
                                <input
                                    id="yFrameSizeInput"
                                    type="text"
                                    value={yInput.value}
                                    onInput={handleInput}
                                    className={yInput.error ? "input-error" : ""}
                                />
                                <OliverText text="px" size="xxs" weight="semi-bold" color="base-blue" className="static-text" />
                            </label>
                            {!isSameSize(buildSize, xInput.value, yInput.value, rotatable) && (
                                <Button buttonStyle="primary" text="Apply" size="fit" onClick={setCustomFrameSize} className="c-size-apply"></Button>
                            )}
                            {(xInput.error || yInput.error) && (
                                <div className="input-error-wrapper" ref={xErrorRef}>
                                    <OliverText text={xInput.error || yInput.error} size="xxs" weight="semi-bold" color="red" />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="toggle-rotate">
                        {buildSize.key === "custom" || (
                            <div className="show-toggle">
                                <OliverText text="Show device skin" size="s" color="base-blue" translatePrefix="frameSize." />
                                <Toggle checked={deviceSkin} callback={toggleCallback} className="device-skin-toggle" />
                            </div>
                        )}
                        {rotatable && (
                            <div className="rotate" onClick={handleRotate}>
                                <Icon src="/size-rotate.svg" className="rotate-icon"></Icon>
                                <OliverText text="Rotate" size="s" color="base-blue" translatePrefix="frameSize." />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FrameSize;
